/**
 * Pure helpers for a Workshop thread told in the M7 rail's language (stage 2a).
 *
 * The rail's parts render; these decide what they are given: which tool calls
 * failed (a failed call must never read as done), where a file sits in a shared
 * thread, what a card is waiting on and who answered it, where a picture really
 * lives, and whether a send never reached Mohtawa.
 */
import { isAxiosError } from "axios";
import type {
  ChatMessage,
  ChatToolEvent,
  StreamingToolEvent,
} from "@/types/chat.type";
import type { ChatAttachmentItem } from "@/app/routes/dashboard/api/useSessionAttachments";
import {
  mapPersistedTool,
  mapStreamingTool,
  type RailToolItem,
} from "@/app/routes/dashboard/components/railTools";
import {
  parseAskUserQuestions,
  SKIP_QUESTIONS_MESSAGE,
  type AskUserQuestion,
} from "@/app/routes/dashboard/components/askUserQuestion";
import {
  parseVerifyRequest,
  SKIP_VERIFY_MESSAGE,
  type VerifyRequest,
} from "@/app/routes/dashboard/components/verifyWithUser";
import {
  placeAttachments,
  readQuestionAnswer,
  readVerifyAnswer,
  type AttachmentClaims,
  type CardAnswer,
  type VerifyAnswer,
} from "@/app/routes/dashboard/agent/rail/railFormat";
import { scrubVendorTerms } from "@/lib/vendorTerms";
import type { WorkshopMediaLibraryLink } from "@/types/workshop.type";

/* --- tools that failed ------------------------------------------------------------- */

/** Keys a failure result carries and nothing else (a thrown tool answers `{ error }`). */
const FAILURE_ONLY_KEYS = new Set(["error", "message", "code", "details"]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Whether a tool's result says it failed. The backend answers a tool that threw
 * with `{ error }` (or marks it `isError`), and tools that refuse with
 * `{ ok: false, error }` or `{ success: false, … }`. A result that merely carries an `error` field among
 * its data (an agent run's status, say) is a call that worked.
 */
export function toolFailed(result: unknown): boolean {
  if (!isRecord(result)) return false;
  // The backend's own mark for a failed call (isToolErrorResult, toolPayload.ts).
  if (result.isError === true) return true;
  if (result.ok === false || result.success === false) return true;
  if (typeof result.error !== "string" || !result.error.trim()) return false;
  return Object.keys(result).every((key) => FAILURE_ONLY_KEYS.has(key));
}

/** "CreateArtifact" → "Create artifact". */
function toolNameWords(toolName: string): string {
  const words = toolName
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .split(/\s+/);
  const [first = "", ...rest] = words;
  const phrase = scrubVendorTerms([first, ...rest.map((w) => w.toLowerCase())].join(" "));
  return phrase.charAt(0).toUpperCase() + phrase.slice(1);
}

/**
 * A failed call's row names the step and says it failed. Its usual label is
 * often in the past tense ("Created artifact: …"), which would claim it worked.
 */
function failedTool(item: RailToolItem): RailToolItem {
  return { ...item, status: "err", label: `${toolNameWords(item.event.toolName)} failed` };
}

export function workshopPersistedTool(event: ChatToolEvent, index: number): RailToolItem {
  const item = mapPersistedTool(event, index);
  if (toolFailed(event.result)) return failedTool(item);
  // A call that never ended (the turn was stopped under it) did not get done.
  if (!event.endedAt) {
    return { ...item, status: "err", label: `${toolNameWords(event.toolName)} stopped` };
  }
  return item;
}

export function workshopStreamingTool(event: StreamingToolEvent): RailToolItem {
  const item = mapStreamingTool(event);
  if (event.status === "running") return item;
  return toolFailed(event.result) ? failedTool(item) : item;
}

/** A call whose result can be shown as an output (a card): it finished and worked. */
export function toolSucceeded(event: ChatToolEvent | StreamingToolEvent): boolean {
  if ("status" in event && event.status === "running") return false;
  if (!event.endedAt) return false;
  return !toolFailed(event.result);
}

/* --- cards: what the agent asked, and what came back --------------------------------- */

export type TurnCards = {
  questions: AskUserQuestion[];
  verify: VerifyRequest | null;
};

/** Everything a turn put up for the person, merged into one card of each kind. */
export function cardsOf(events: (ChatToolEvent | StreamingToolEvent)[]): TurnCards {
  const questions = events.flatMap((event) => parseAskUserQuestions(event) ?? []);
  const requests = events
    .map((event) => parseVerifyRequest(event))
    .filter((r): r is VerifyRequest => r !== null);
  if (requests.length === 0) return { questions, verify: null };
  const intro = requests.map((r) => r.intro).find((i): i is string => Boolean(i));
  const items = requests.flatMap((r) => r.items);
  return { questions, verify: intro ? { intro, items } : { items } };
}

export type CardOutcome = {
  questions: CardAnswer | null;
  verify: VerifyAnswer | null;
  /** Shared threads: who answered, "You" or their name. */
  answeredBy: string | null;
};

export type ThreadCardAnswers = {
  byCard: Map<string, CardOutcome>;
  /** Messages that only carry a card's answer: the card shows the pick instead. */
  echoes: Set<string>;
};

const nameOf = (message: ChatMessage, viewerUserId: string | null): string =>
  !message.authorUserId || message.authorUserId === viewerUserId
    ? "You"
    : message.authorName?.trim() || "A teammate";

/**
 * How each card was answered. A private thread answers a card with the next
 * message; a shared thread names the card it answers (`respondsTo`), and only
 * the first answer counts. A card from before a thread was shared was answered
 * the private way.
 */
export function readThreadCardAnswers(
  messages: ChatMessage[],
  pendingId: string | null,
  shared: { viewerUserId: string | null; sharedAt: string | null } | null,
): ThreadCardAnswers {
  const byCard = new Map<string, CardOutcome>();
  const echoes = new Set<string>();
  const responses = new Map<string, ChatMessage>();
  if (shared) {
    for (const message of messages) {
      if (message.respondsTo && !responses.has(message.respondsTo)) {
        responses.set(message.respondsTo, message);
      }
    }
  }
  messages.forEach((message, index) => {
    if (message.role !== "assistant" || message.id === pendingId) return;
    const { questions, verify } = cardsOf(message.toolEvents ?? []);
    if (questions.length === 0 && !verify) return;
    const next = messages[index + 1];
    const reply =
      responses.get(message.id) ??
      (shared?.sharedAt && message.createdAt >= shared.sharedAt
        ? null
        : next?.role === "user"
          ? next
          : null);
    if (!reply) {
      byCard.set(message.id, { questions: null, verify: null, answeredBy: null });
      return;
    }
    const questionAnswer =
      questions.length > 0 ? readQuestionAnswer(questions, reply.content) : null;
    const verifyAnswer =
      verify && questions.length === 0 ? readVerifyAnswer(verify.items, reply.content) : null;
    const skipped =
      reply.content.trim() === SKIP_QUESTIONS_MESSAGE ||
      reply.content.trim() === SKIP_VERIFY_MESSAGE;
    if (questionAnswer || verifyAnswer || skipped) echoes.add(reply.id);
    byCard.set(message.id, {
      questions: questionAnswer,
      verify: verifyAnswer,
      answeredBy: shared ? nameOf(reply, shared.viewerUserId) : null,
    });
  });
  return { byCard, echoes };
}

/* --- files: which message each went out with ------------------------------------------ */

export type PlacedThreadFiles = {
  /** The viewer's files not sent with a message yet: they wait in the composer. */
  staged: ChatAttachmentItem[];
  /** Files that went out with a message, keyed by that message. */
  byMessage: Map<string, ChatAttachmentItem[]>;
  /** Someone else's files not followed by a message of theirs: shown where they landed. */
  loose: ChatAttachmentItem[];
};

/**
 * A private thread places files the rail's way (the first message of yours after
 * them, or the message this tab sent them with). In a shared thread a file
 * belongs to its own author's next message, so one person's upload never sits
 * above another person's words.
 */
export function placeThreadFiles(
  messages: ChatMessage[],
  items: ChatAttachmentItem[],
  claims: AttachmentClaims,
  viewerUserId: string | null,
  shared: boolean,
  /** Private: your messages in the pages not loaded (see placeAttachments). */
  claimOffset = 0,
): PlacedThreadFiles {
  if (!shared) {
    const { staged, byMessage } = placeAttachments(messages, items, claims, claimOffset);
    return { staged, byMessage, loose: [] };
  }
  const staged: ChatAttachmentItem[] = [];
  const loose: ChatAttachmentItem[] = [];
  const byMessage = new Map<string, ChatAttachmentItem[]>();
  const authorOf = (id: string | null | undefined) => id || viewerUserId;
  for (const item of items) {
    const author = authorOf(item.authorUserId);
    const owner = item.local
      ? undefined
      : messages.find(
          (m) =>
            m.role === "user" &&
            authorOf(m.authorUserId) === author &&
            m.createdAt >= item.createdAt,
        );
    if (owner) {
      byMessage.set(owner.id, [...(byMessage.get(owner.id) ?? []), item]);
    } else if (author === viewerUserId) {
      staged.push(item);
    } else {
      loose.push(item);
    }
  }
  return { staged, byMessage, loose };
}

/* --- where a picture lives ------------------------------------------------------------- */

/**
 * Where a project picture really is, from its record. A picture the agent makes
 * stays in the project; the brand library has it only once someone copies it.
 */
export function pictureHome(library: WorkshopMediaLibraryLink | null): string {
  if (!library) return "In this project. Not in the brand library.";
  if (library.relation === "copied_to") return "In this project and the brand library.";
  return "In this project, from the brand library.";
}

/* --- a send that never reached Mohtawa --------------------------------------------------- */

/**
 * The request got no response at all. Being offline is not enough on its own:
 * a request that did answer (a 4xx, a 5xx) is the server's word, not silence.
 */
export function isUnreachable(error: unknown): boolean {
  return isAxiosError(error) && !error.response;
}

/* --- the model chip on a phone -------------------------------------------------------------- */

/** "Gemini 3.8 Flash" → "3.8 Flash": the mark beside it already names the maker. */
export function shortModelName(name: string): string {
  const words = name.trim().split(/\s+/);
  return words.length > 2 ? words.slice(-2).join(" ") : name;
}
